import { useState, useMemo } from "react";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CASE_STUDIES } from "@/lib/siteData";
import { YouTubeEmbed } from "@/components/site/YouTubeEmbed";
import { Play, ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROUTE_SEO } from "@/lib/routeSeo";
import { Link } from "react-router-dom";

const ALL = "All industries";

export default function CaseStudies() {
  const industries = useMemo(() => {
    const set = new Set(CASE_STUDIES.map(c => c.industry).filter(Boolean));
    return [ALL, ...Array.from(set)];
  }, []);

  const [filter, setFilter] = useState(ALL);
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(
    () => (filter === ALL ? CASE_STUDIES : CASE_STUDIES.filter(c => c.industry === filter)),
    [filter]
  );

  const open = CASE_STUDIES.find(c => c.slug === openId);
  const hasVideo = !!open?.youtubeId;

  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/case-studies"].title}
        description={ROUTE_SEO["/case-studies"].meta}
        path="/case-studies"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Case Studies" }])}
      />
      <PageHero
        kicker="Case studies"
        title={ROUTE_SEO["/case-studies"].h1}
        subtitle="A selection of our most-loved project breakdowns - what the brief was, how we cracked it, and what it delivered."
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Case Studies" }]}
      />

      {/* Industry filter pills */}
      <section className="border-b border-border bg-[hsl(225,22%,4%)]">
        <div className="container-site py-6">
          <div data-testid="case-studies-filter" className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mr-1">Filter</span>
            {industries.map(ind => {
              const active = filter === ind;
              return (
                <button
                  key={ind}
                  type="button"
                  data-testid={`filter-${ind.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setFilter(ind)}
                  className={`h-9 rounded-full border px-4 text-xs uppercase tracking-[0.18em] transition-colors ${
                    active
                      ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
                      : "bg-card border-border text-foreground/80 hover:bg-[hsl(225,18%,9%)] hover:text-foreground"
                  }`}
                >
                  {ind}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site space-y-10">
          {filtered.length === 0 && (
            <div className="text-center text-muted-foreground py-16">No case studies in this category yet.</div>
          )}

          {filtered.map(c => {
            const playable = !!c.youtubeId;
            return (
              <article
                key={c.slug}
                data-testid={`case-study-${c.slug}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start rounded-2xl border border-border bg-card p-6 sm:p-8"
              >
                <div className="lg:col-span-7">
                  {playable ? (
                    <button
                      onClick={() => setOpenId(c.slug)}
                      data-testid={`case-study-play-${c.slug}`}
                      className="group block w-full relative rounded-xl overflow-hidden border border-border"
                    >
                      <img src={c.thumbnail} alt={c.title} className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
                        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                          <Play className="h-5 w-5 fill-current" />
                        </span>
                      </div>
                    </button>
                  ) : (
                    <div className="relative w-full rounded-xl overflow-hidden border border-border">
                      <img src={c.thumbnail} alt={c.title} className="w-full aspect-video object-cover" loading="lazy" />
                      {c.clientLogo && (
                        <div className="absolute bottom-4 left-4 inline-flex items-center justify-center h-12 px-3 rounded-md bg-white/95 backdrop-blur-sm">
                          <img src={c.clientLogo} alt={`${c.client} logo`} className="h-7 w-auto object-contain" loading="lazy" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="lg:col-span-5 space-y-4">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.22em]">
                    <span className="text-[hsl(var(--primary))]">{c.category}</span>
                    <span className="text-muted-foreground">· {c.year}</span>
                    {c.industry && <span className="text-muted-foreground">· {c.industry}</span>}
                  </div>
                  <h2 className="font-display uppercase text-foreground text-2xl sm:text-3xl tracking-[0.02em] leading-tight">{c.title}</h2>
                  <div>
                    <div className="kicker mb-1">Client</div>
                    <div className="text-sm text-foreground">{c.client}</div>
                  </div>
                  <div>
                    <div className="kicker mb-1">Brief</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.brief}</p>
                  </div>
                  <div>
                    <div className="kicker mb-1">Approach</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.approach}</p>
                  </div>
                  <div>
                    <div className="kicker mb-2">Results</div>
                    <ul className="space-y-1.5 text-sm text-foreground/85">
                      {c.results.map(r => (
                        <li key={r} className="flex items-start gap-2">
                          <span className="mt-2 inline-block h-1 w-1 rounded-full bg-[hsl(var(--primary))]" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {playable && (
                    <button
                      onClick={() => setOpenId(c.slug)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-foreground hover:text-[hsl(var(--primary))] transition-colors"
                    >
                      Watch the film <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </article>
            );
          })}

          <div className="text-center pt-8">
            <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6">
              <Link to="/contact">
                Brief us on yours <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-5xl w-[94vw] p-0 bg-black border-border overflow-hidden">
          <DialogTitle className="sr-only">{open?.title || ""}</DialogTitle>
          {open && hasVideo && (
            <YouTubeEmbed videoId={open.youtubeId} title={open.title} />
          )}
        </DialogContent>
      </Dialog>
    </SiteShell>
  );
}
